import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { getAlerts } from '../../data/mockApi';

// Dark theme alert styles
const ALERT_STYLES = {
  critical: {
    bg: 'bg-red-500/5',
    border: 'border-red-500/20',
    icon: 'text-red-400',
    title: 'text-red-400'
  },
  warning: {
    bg: 'bg-amber-500/5',
    border: 'border-amber-500/20',
    icon: 'text-amber-400',
    title: 'text-amber-400'
  },
  info: {
    bg: 'bg-sky-500/5',
    border: 'border-sky-500/20',
    icon: 'text-sky-400',
    title: 'text-sky-400'
  },
  success: {
    bg: 'bg-lime-500/5',
    border: 'border-lime-500/20',
    icon: 'text-lime-400',
    title: 'text-lime-400'
  }
};

const AlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getAlerts();
      setAlerts(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const getAlertStyle = (type) => ALERT_STYLES[type] || ALERT_STYLES.info;

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-[#111916] rounded-xl border border-[#243129]"
    >
      <div className="p-5 border-b border-[#243129]">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">Risk Alerts</h3>
          {!loading && (
            <span className="bg-red-500/10 text-red-400 px-2 py-0.5 rounded text-xs font-mono font-medium border border-red-500/20">
              {alerts.filter(a => a.type === 'critical').length} Critical
            </span>
          )}
        </div>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-center text-[#6b8772] font-mono text-sm py-4">Loading alerts...</div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert, index) => {
              const style = getAlertStyle(alert.type);
              return (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`${style.bg} border ${style.border} rounded-lg p-3 hover:border-[#243129] transition-colors cursor-pointer group`}
                >
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={alert.icon} className={`w-4 h-4 mt-0.5 ${style.icon}`} />
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-mono font-medium text-sm ${style.title} group-hover:brightness-110 transition-all`}>{alert.title}</h4>
                      <p className="text-xs font-mono text-[#6b8772] mt-0.5 line-clamp-2">{alert.message}</p>
                      <span className="text-[10px] font-mono text-[#4a5f50] mt-1 block">{alert.time}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full mt-4 text-xs font-mono font-medium text-[#84cc16] hover:text-lime-300 transition-colors py-2 border border-[#243129] rounded-lg hover:border-[#84cc16]/30 hover:bg-lime-500/5"
        >
          View All Alerts
        </motion.button>
      </div>
    </motion.div>
  );
};

export default AlertsPanel;
